/**
 * DtmfKey — the dark "key-cap" pill used to render a DTMF digit.
 *
 * Originally lived inside CallSessionDrawer; extracted so the IVR list
 * view (Style 1 / Nested Cards) can surface the same visual on branch
 * edges. Keep the styling in lockstep across both call-sites so users
 * recognise the same glyph in playback history and in the tree editor.
 *
 * `size`:
 *   - 'md' (default) — 7×7, used in the call log drawer.
 *   - 'sm'           — 5×5, used on tree edges where space is tighter.
 */
import { cn } from '@/lib/cn';

export interface DtmfKeyProps {
  k: string;
  size?: 'sm' | 'md';
  className?: string;
  /**
   * Aria label for the pill. Defaults to `Key {k}` — kept this way so the
   * call-log drawer's existing accessibility tests (`getByLabelText('Key 1')`)
   * don't regress. Tree edges pass `branch key {k}` to match the older
   * Style A contract.
   */
  ariaLabel?: string;
}

export function DtmfKey({ k, size = 'md', className, ariaLabel }: DtmfKeyProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-lg',
        'border border-slate-700 bg-slate-800 shadow-sm',
        'font-mono font-semibold text-slate-100 leading-none select-none',
        size === 'md' && 'h-7 w-7 text-sm',
        size === 'sm' && 'h-5 min-w-[1.25rem] px-1 text-[11px]',
        className,
      )}
      aria-label={ariaLabel ?? `Key ${k}`}
    >
      {k}
    </span>
  );
}
